/* eslint-disable no-plusplus */
/* eslint-disable fp/no-mutating-methods */
/* eslint-disable fp/no-mutation */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React, {
  ReactElement,
  useEffect,
  useState,
  useMemo,
  useRef,
} from 'react'
import { useFormik, Field, FormikProvider, FieldProps } from 'formik'
import * as yup from 'yup'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchVideos,
  fetchTextContent,
  fetchShowNotes,
  createShowNotes,
  selectActiveClass,
  selectVideosByClassID,
  selectVideosStatus,
  selectTextContentByClassID,
  selectTextContentStatus,
  selectShowNotesByClassID,
  selectShowNotesStatus,
} from 'src/v3/create/create.slice'
import {
  AddIcon,
  DeleteForeverIcon,
  DownloadIcon,
  EditIcon,
  LeftArrowIcon,
  RightArrowIcon,
  VideoIcon,
  TextContentIcon,
  TranscribeIcon,
  LibraryIcon,
} from 'src/v3/core/icons'
import { Image as ImageIcon } from '@mui/icons-material'
import {
  ClassUpdated as Class,
  Video,
  TextContent,
  ShowNotes,
} from 'src/v3/core/types'
import ConfirmAction from 'src/v3/components/confirm-action'
import Wysiwyg from 'src/components/common/wysiwyg'
import { useNavigate } from 'react-router-dom'
import {
  selectRoles,
  selectUseShowNotes,
  selectUseTranscriptEditor,
} from 'src/v3/settings/settings.slice'
import { TextEditorOutput } from 'src/v3/components/show-notes-editor/types'
import { TranscriptEditorModal } from 'src/v3/components/transcript-editor-modal'
import { ShowNotesTab } from './content/show-notes'
import { VocabularyManagementModal } from '../components/vocabulary-management-modal'
import { AddVocabularyModal } from '../components/add-vocabulary-modal'
import DownloadVideoModal from './components/download-video-modal'

interface Props {
  selectedClass?: Class
  setStep: React.Dispatch<
    React.SetStateAction<
      | 'basic-info'
      | 'title-screen'
      | 'content'
      | 'quiz'
      | 'storyboard'
      | 'preview'
    >
  >
}

type ContentTab = 'videos' | 'text-content' | 'show-notes'

export default ({ selectedClass, setStep }: Props): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let roles = useAppSelector(selectRoles)
  let isAdmin = roles.includes('admin')
  let activeClass = useAppSelector(selectActiveClass)
  let videos = useAppSelector(selectVideosByClassID(activeClass))
  let videosStatus = useAppSelector(selectVideosStatus)
  let textContent = useAppSelector(
    selectTextContentByClassID(selectedClass?.id || ''),
  )
  let textContentStatus = useAppSelector(selectTextContentStatus)
  let showNotes = useAppSelector(
    selectShowNotesByClassID(selectedClass?.id || ''),
  )
  let showNotesStatus = useAppSelector(selectShowNotesStatus)

  // Helper function to determine if video is uploaded
  let isVideoUploaded = (video: Video) =>
    video.fileName &&
    video.fileName !== `${import.meta.env.VITE_CLOUDFRONT_URL}/public/`

  let [activeTab, setActiveTab] = useState<ContentTab>('videos')
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [uploading, setUploading] = useState<number>(0)
  let [deleting, setDeleting] = useState<string>('')
  let [formOpen, setFormOpen] = useState<boolean>(false)
  let [modalMode, setModalMode] = useState<'create' | 'upload' | 'edit'>(
    'create',
  )
  let [selectedVideoForUpload, setSelectedVideoForUpload] =
    useState<Video | null>(null)
  let [file, setFile] = useState<File | null>(null)

  // Show Notes state - for editing individual show notes
  let [selectedShowNotes, setSelectedShowNotes] = useState<ShowNotes | null>(
    null,
  )
  let [showNotesContent, setShowNotesContent] = useState<string>(
    '# Welcome to Show Notes\n\nStart typing your **show notes** here. You can use:\n\n- **Bold** and *italic* text\n- [Links](https://example.com)\n- Lists and bullet points\n\nAll content is saved as markdown for easy reading and portability.',
  )
  let [showNotesSaving, setShowNotesSaving] = useState<boolean>(false)
  let [showNotesOutput, setShowNotesOutput] = useState<TextEditorOutput | null>(
    null,
  )

  // Transcript management state
  let [transcriptAvailability, setTranscriptAvailability] = useState<
    Record<string, boolean>
  >({})
  let [transcribing, setTranscribing] = useState<Record<string, boolean>>({})
  let [transcribeConfirmOpen, setTranscribeConfirmOpen] = useState<string>('')
  let useTranscriptEditor = useAppSelector(selectUseTranscriptEditor)
  let checkedVideosRef = useRef<Set<string>>(new Set())
  let useShowNotes = useAppSelector(selectUseShowNotes)

  // State for block counts
  let [blockCounts, setBlockCounts] = useState<
    Record<string, { total: number; text: number; image: number }>
  >({})

  // Vocabulary modal state
  let [vocabularyModalOpen, setVocabularyModalOpen] = useState<boolean>(false)
  let [addVocabularyModalOpen, setAddVocabularyModalOpen] =
    useState<boolean>(false)
  let [vocabularyInitialWord, setVocabularyInitialWord] = useState<string>('')

  // Transcript editor modal state
  let [transcriptModalOpen, setTranscriptModalOpen] = useState<boolean>(false)
  let [selectedVideoId, setSelectedVideoId] = useState<string>('')

  // Download video modal state
  let [downloadModalOpen, setDownloadModalOpen] = useState<boolean>(false)
  let [selectedVideoForDownload, setSelectedVideoForDownload] = useState<{
    id: string
    name: string
  } | null>(null)

  // Fetch block counts for all show notes
  useEffect(() => {
    if (!showNotes || showNotes.length === 0) return

    let fetchBlockCounts = async () => {
      let counts: Record<
        string,
        { total: number; text: number; image: number }
      > = {}

      try {
        await Promise.all(
          showNotes.map(async (notes) => {
            try {
              let response = await axios.get(
                `/api/v2/create/show-notes/${notes.id}/blocks`,
              )

              let blocks = response.data?.data || response.data || []

              // Handle wrapped format: [{ textBlock: {...} }, { imageBlock: {...} }]
              let textCount = 0
              let imageCount = 0

              if (Array.isArray(blocks)) {
                blocks.forEach((item: any) => {
                  if (item.textBlock) {
                    textCount++
                  }
                  if (item.imageBlock) {
                    imageCount++
                  }
                })
              }

              counts[notes.id] = {
                total: textCount + imageCount,
                text: textCount,
                image: imageCount,
              }
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Error fetching blocks for', notes.name, ':', error)
              counts[notes.id] = { total: 0, text: 0, image: 0 }
            }
          }),
        )

        setBlockCounts(counts)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching block counts:', error)
      }
    }

    fetchBlockCounts()
  }, [showNotes])

  // Video form
  let videoForm = useFormik({
    validationSchema: yup.object({
      name: yup.string().required('Video name is required'),
      description: yup.string().required('Description is required'),
    }),
    initialValues: {
      id: '',
      name: '',
      description: '',
    },
    onSubmit: async (values) => {
      if (!selectedClass) return

      setSaving(true)
      try {
        let savedVideo
        if (!values.id) {
          // Create new video
          let response = await axios.post('/api/v2/create/video', {
            name: values.name,
            description: values.description,
            classID: selectedClass.id,
            key: '',
          })
          savedVideo = response.data
        } else {
          // Update existing video
          await axios.put('/api/v2/create/video', {
            id: values.id,
            name: values.name,
            description: values.description,
          })
        }

        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchVideos())

        // If this is a new video creation, transition to upload step
        if (!values.id && savedVideo && modalMode === 'create') {
          setSelectedVideoForUpload(savedVideo)
          setModalMode('upload')
          // Don't close modal, transition to upload step
        } else {
          setFormOpen(false)
          videoForm.resetForm()
        }
      } catch (err: any) {
        setErrorMessage(
          err?.response?.data || err?.message || 'Error saving video',
        )
      } finally {
        setSaving(false)
      }
    },
  })

  // Text content form
  let textForm = useFormik({
    validationSchema: yup.object({
      name: yup.string().required('Content name is required'),
      content: yup.string().required('Content is required'),
    }),
    initialValues: {
      id: '',
      name: '',
      content: '',
    },
    onSubmit: async (values) => {
      if (!selectedClass) return

      setSaving(true)
      try {
        if (!values.id) {
          await axios.post('/api/v2/create/text-content', {
            name: values.name,
            content: values.content,
            classID: selectedClass.id,
          })
        } else {
          await axios.put('/api/v2/create/text-content', {
            id: values.id,
            name: values.name,
            content: values.content,
          })
        }

        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchTextContent(selectedClass.id))
        setFormOpen(false)
        textForm.resetForm()
      } catch (err: any) {
        setErrorMessage(
          err?.response?.data || err?.message || 'Error saving text content',
        )
      } finally {
        setSaving(false)
      }
    },
  })

  // Show Notes form
  let showNotesForm = useFormik({
    validationSchema: yup.object({
      name: yup.string().required('Show notes name is required'),
      description: yup.string().required('Description is required'),
    }),
    initialValues: {
      name: '',
      description: '',
    },
    onSubmit: async (values) => {
      if (!selectedClass) return

      setSaving(true)
      try {
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(
          createShowNotes({
            classID: selectedClass.id,
            name: values.name,
            description: values.description,
          }),
        )

        setFormOpen(false)
        showNotesForm.resetForm()
      } catch (err: any) {
        setErrorMessage(
          err?.response?.data || err?.message || 'Error creating show notes',
        )
      } finally {
        setSaving(false)
      }
    },
  })

  useEffect(() => {
    if (videosStatus === 'uninitialized') {
      dispatch(fetchVideos())
    }
  }, [videosStatus])

  useEffect(() => {
    if (selectedClass && textContentStatus === 'uninitialized') {
      dispatch(fetchTextContent(selectedClass.id))
    }
  }, [selectedClass, textContentStatus])

  useEffect(() => {
    if (selectedClass && showNotesStatus === 'uninitialized') {
      dispatch(fetchShowNotes(selectedClass.id))
    }
  }, [selectedClass, showNotesStatus])

  // Handle tab switching when text content tab should be hidden
  useEffect(() => {
    if (
      activeTab === 'text-content' &&
      useShowNotes &&
      textContentStatus === 'fulfilled' &&
      (!textContent || textContent.length === 0)
    ) {
      setActiveTab('videos') // Switch to videos tab if text content tab is hidden
    }
  }, [activeTab, useShowNotes, textContent, textContentStatus])

  // Create stable reference to uploaded video IDs
  let uploadedVideoIds = useMemo(
    () =>
      videos
        .filter(isVideoUploaded)
        .map((v) => v.id)
        .sort(),
    [videos],
  )

  // Check transcript availability for uploaded videos only
  useEffect(() => {
    if (
      videosStatus === 'fulfilled' &&
      uploadedVideoIds.length > 0 &&
      useTranscriptEditor
    ) {
      // Only fetch if we haven't already checked these videos
      let videosToCheck = uploadedVideoIds.filter(
        (id) => !checkedVideosRef.current.has(id),
      )

      if (videosToCheck.length === 0) return

      // Mark videos as being checked
      videosToCheck.forEach((id) => checkedVideosRef.current.add(id))

      let fetchTranscripts = async () => {
        let results = await Promise.all(
          videosToCheck.map(async (videoId) => {
            try {
              let response = await axios.get(
                `/api/create/video/${videoId}/transcript`,
              )
              if (response.data) {
                try {
                  await axios.get(response.data)
                  return { id: videoId, hasTranscript: true }
                } catch {
                  return { id: videoId, hasTranscript: false }
                }
              }
              return { id: videoId, hasTranscript: false }
            } catch {
              return { id: videoId, hasTranscript: false }
            }
          }),
        )

        let transcriptStatus = results.reduce(
          (acc, { id, hasTranscript }) => {
            acc[id] = hasTranscript
            return acc
          },
          {} as Record<string, boolean>,
        )

        setTranscriptAvailability((prev) => ({ ...prev, ...transcriptStatus }))
      }

      fetchTranscripts()
    }
  }, [videosStatus, uploadedVideoIds, useTranscriptEditor])

  let onEditVideo = (video: Video) => {
    videoForm.setValues({
      id: video.id,
      name: video.name,
      description: video.description,
    })
    setModalMode('edit')
    setSelectedVideoForUpload(null)
    setFile(null)
    setActiveTab('videos')
    setFormOpen(true)
  }

  let onEditTextContent = (content: TextContent) => {
    textForm.setValues({
      id: content.id,
      name: content.name,
      content: content.content,
    })
    setActiveTab('text-content')
    setFormOpen(true)
  }

  let onDeleteVideo = async (id: string) => {
    try {
      await axios.delete(`/api/v2/create/delete-video?videoID=${id}`)
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(fetchVideos())
    } catch (err: any) {
      setErrorMessage(err?.message || 'Error deleting video')
    }
  }

  let onDeleteTextContent = async (id: string) => {
    if (!selectedClass) return

    try {
      await axios.delete(
        `/api/v2/create/delete-text-content?textContentID=${id}`,
      )
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(fetchTextContent(selectedClass.id))
    } catch (err: any) {
      setErrorMessage(err?.message || 'Error deleting text content')
    }
  }

  let getPresignedUrls = async (numberOfParts: number) => {
    let response = await axios.get(
      `/api/video-multipart-urls?numberOfParts=${numberOfParts}`,
    )
    return response.data
  }

  let uploadPart = async (presignedUrl: string, part: Blob) => {
    let response = await axios.put(presignedUrl, part, {
      headers: {
        'Cache-Control': 'max-age=2592000',
        'content-type': 'video/mp4',
      },
    })
    return response.headers
  }

  let completeMultipartUpload = async (
    key: string,
    uploadId: string,
    parts: { etag: string; partNumber: number }[],
  ) => {
    await axios.post('/api/complete-video-multipart-upload', {
      key,
      uploadId,
      parts,
    })
  }

  let handleVideoUpload = async () => {
    if (!file || !selectedVideoForUpload) return

    setUploading(1)
    try {
      // Step 1: Get presigned URLs for multipart upload
      let numberOfParts = Math.floor(file.size / 5242880) || 1
      let { signedURLs, uploadID, key } = await getPresignedUrls(numberOfParts)

      let partSize = Math.ceil(file.size / numberOfParts)
      let parts: { etag: string; partNumber: number }[] = []
      let completedParts = 0

      // Step 2: Upload all parts
      let uploadPromises = Array.from(
        { length: numberOfParts },
        async (_, i) => {
          let part = file.slice(i * partSize, (i + 1) * partSize)
          let response = await uploadPart(signedURLs[i], part)
          parts.push({ etag: response.etag, partNumber: i + 1 })
          completedParts += 1
          setUploading(Math.ceil((100 * completedParts) / (numberOfParts + 1)))
        },
      )

      await Promise.all(uploadPromises)

      // Step 3: Complete multipart upload
      await completeMultipartUpload(key, uploadID, parts)

      // Step 4: Link the uploaded file to the video entity
      await axios.put('/api/v2/create/video/file', {
        id: selectedVideoForUpload.id,
        fileName: key,
      })

      // Step 5: Fresh fetch to update video status (removes "Draft" status)
      setUploading(100)
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(fetchVideos())

      // Close modal after successful upload
      setFile(null)
      setUploading(0)
      setFormOpen(false)
      setSelectedVideoForUpload(null)
    } catch (err: any) {
      setErrorMessage(err?.message || 'Error uploading video')
      setUploading(0)
    }
  }

  let onManageTranscript = (videoId: string) => {
    setSelectedVideoId(videoId)
    setTranscriptModalOpen(true)
  }

  let handleTranscribe = async (videoId: string) => {
    setTranscribing({ ...transcribing, [videoId]: true })

    try {
      await axios.post(`/api/create/video/${videoId}/transcript/trigger`)

      // Poll for transcript completion
      let pollCount = 0
      let maxPollCount = 60 // 10 minutes at 10 second intervals

      let pollInterval = setInterval(async () => {
        try {
          pollCount++
          let response = await axios.get(
            `/api/create/video/${videoId}/transcript`,
          )

          if (response.status === 200 && response.data) {
            try {
              await axios.get(response.data)
              setTranscriptAvailability((prev) => ({
                ...prev,
                [videoId]: true,
              }))
              setTranscribing({ ...transcribing, [videoId]: false })
              clearInterval(pollInterval)
            } catch {
              if (pollCount >= maxPollCount) {
                setErrorMessage(
                  'Transcription timed out. Please try again later.',
                )
                setTranscribing({ ...transcribing, [videoId]: false })
                clearInterval(pollInterval)
              }
            }
          } else if (pollCount >= maxPollCount) {
            setErrorMessage('Transcription timed out. Please try again later.')
            setTranscribing({ ...transcribing, [videoId]: false })
            clearInterval(pollInterval)
          }
        } catch {
          if (pollCount >= maxPollCount) {
            setErrorMessage('Transcription failed. Please try again later.')
            setTranscribing({ ...transcribing, [videoId]: false })
            clearInterval(pollInterval)
          }
        }
      }, 10000) // Poll every 10 seconds
    } catch {
      setErrorMessage('Failed to trigger transcription. Please try again.')
      setTranscribing({ ...transcribing, [videoId]: false })
    }
  }

  // Vocabulary handlers
  let handleOpenVocabularyModal = () => {
    setVocabularyModalOpen(true)
  }

  let handleCloseVocabularyModal = () => {
    setVocabularyModalOpen(false)
  }

  let handleOpenAddVocabularyModal = (word?: string) => {
    setVocabularyInitialWord(word || '')
    setAddVocabularyModalOpen(true)
  }

  let handleCloseAddVocabularyModal = () => {
    setAddVocabularyModalOpen(false)
    setVocabularyInitialWord('')
  }

  let handleVocabularySuccess = () => {
    // Modal will close itself, just refresh the vocabulary list if the management modal is open
    if (vocabularyModalOpen) {
      // The management modal will refresh its own data when it detects the success
    }
  }

  if (!selectedClass) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please complete the Basic Info step first to manage content.
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<LeftArrowIcon />}
            onClick={() => setStep('basic-info')}
          >
            Back to Basic Info
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Class Content
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Upload videos and add text content for this class.
      </Typography>
      {/* Content Type Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ mb: 3 }}
      >
        <Tab
          value="videos"
          label={`Videos (${videos.length})`}
          icon={<VideoIcon />}
          iconPosition="start"
        />
        {(!useShowNotes ||
          (useShowNotes &&
            !(
              textContentStatus === 'fulfilled' &&
              (!textContent || textContent.length === 0)
            ))) && (
          <Tab
            value="text-content"
            label={`Text Content (${textContent?.length || 0})`}
            icon={<TextContentIcon />}
            iconPosition="start"
          />
        )}
        {useShowNotes && (
          <Tab
            value="show-notes"
            label={`Show Notes (${showNotes?.length || 0})`}
            icon={<EditIcon />}
            iconPosition="start"
          />
        )}
      </Tabs>
      {/* Videos Tab */}
      {activeTab === 'videos' && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                videoForm.resetForm()
                setModalMode('create')
                setSelectedVideoForUpload(null)
                setFile(null)
                setFormOpen(true)
              }}
            >
              Add Video
            </Button>
          </Box>

          {videos.length === 0 ? (
            <Alert severity="info">
              No videos added yet. Add your first instructional video to get
              started.
            </Alert>
          ) : (
            <Box className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {videos.map((video) => (
                <Box>
                  <Card variant="outlined">
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 2,
                            }}
                          >
                            <Typography variant="h6" sx={{ flex: 1 }}>
                              {video.name}
                            </Typography>
                            {!isVideoUploaded(video) ? (
                              <Chip
                                size="small"
                                label="Draft"
                                color="warning"
                                variant="outlined"
                              />
                            ) : (
                              <Chip
                                size="small"
                                label="Complete"
                                color="success"
                                variant="outlined"
                              />
                            )}
                          </Box>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            {video.description}
                          </Typography>

                          {/* Video Player or Upload Prompt */}
                          {isVideoUploaded(video) ? (
                            <CardMedia
                              sx={{ mb: 2, borderRadius: 1 }}
                              component="video"
                              image={video.fileName}
                              controls
                              height={200}
                            />
                          ) : (
                            <Box
                              sx={{
                                height: 200,
                                bgcolor: 'grey.100',
                                borderRadius: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 2,
                                border: '2px dashed',
                                borderColor: 'grey.300',
                              }}
                            >
                              <VideoIcon
                                sx={{ fontSize: 48, color: 'grey.400', mb: 1 }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                align="center"
                              >
                                You have not yet attached a file to this video.
                                <br />
                                Please upload a file to complete creating your
                                video content.
                              </Typography>
                            </Box>
                          )}

                          {/* Action Buttons */}
                          <Box
                            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
                          >
                            {/* Upload/Replace Button */}
                            <Button
                              size="small"
                              variant={
                                isVideoUploaded(video)
                                  ? 'outlined'
                                  : 'contained'
                              }
                              color={
                                isVideoUploaded(video) ? 'primary' : 'warning'
                              }
                              startIcon={<VideoIcon />}
                              onClick={() => {
                                setSelectedVideoForUpload(video)
                                setModalMode('upload')
                                setFile(null)
                                setFormOpen(true)
                              }}
                            >
                              {isVideoUploaded(video) ? 'Replace' : 'Upload'}
                            </Button>

                            {/* Edit Transcript Button */}
                            {useTranscriptEditor && isVideoUploaded(video) && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onManageTranscript(video.id)}
                                disabled={
                                  transcriptAvailability[video.id] === false ||
                                  transcribing[video.id]
                                }
                              >
                                Edit Transcript
                              </Button>
                            )}

                            {/* Transcribe Button */}
                            {useTranscriptEditor && isVideoUploaded(video) && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={
                                  transcribing[video.id] ? undefined : (
                                    <TranscribeIcon />
                                  )
                                }
                                onClick={() =>
                                  setTranscribeConfirmOpen(video.id)
                                }
                                color={
                                  !!transcriptAvailability[video.id] ||
                                  transcribing[video.id]
                                    ? 'error'
                                    : 'primary'
                                }
                              >
                                {transcribing[video.id]
                                  ? 'Transcribing...'
                                  : 'Transcribe'}
                              </Button>
                            )}

                            {/* Vocabulary Management Button */}
                            {useTranscriptEditor && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<LibraryIcon />}
                                onClick={handleOpenVocabularyModal}
                              >
                                Vocabulary
                              </Button>
                            )}

                            {/* Download Video Button (Admin Only) */}
                            {isAdmin && isVideoUploaded(video) && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => {
                                  setSelectedVideoForDownload({
                                    id: video.id,
                                    name: video.name,
                                  })
                                  setDownloadModalOpen(true)
                                }}
                              >
                                Download Video
                              </Button>
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ alignSelf: 'flex-start', ml: 'auto' }}>
                          <Tooltip title="Edit Details">
                            <IconButton
                              size="small"
                              onClick={() => onEditVideo(video)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Video">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleting(`video-${video.id}`)}
                            >
                              <DeleteForeverIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <ConfirmAction
                        executeAction={() => onDeleteVideo(video.id)}
                        open={deleting}
                        setOpen={setDeleting}
                        openText={`video-${video.id}`}
                        title="Confirm Deletion"
                        text={`Are you sure you want to delete "${video.name}"?`}
                        onFailure={(err) =>
                          setErrorMessage(err.message || 'Error deleting video')
                        }
                      />
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
      {/* Text Content Tab */}
      {activeTab === 'text-content' &&
        (!useShowNotes ||
          (useShowNotes &&
            !(
              textContentStatus === 'fulfilled' &&
              (!textContent || textContent.length === 0)
            ))) && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={useShowNotes}
                onClick={() => {
                  textForm.resetForm()
                  setFormOpen(true)
                }}
              >
                Add Text Content
              </Button>
              {useShowNotes && (
                <Alert severity="info" sx={{ ml: 2 }}>
                  Text content creation is disabled when Show Notes are enabled.
                  Use Show Notes instead for rich content.
                </Alert>
              )}
            </Box>

            {!textContent || textContent.length === 0 ? (
              <Alert severity="info">
                No text content added yet. Add supplemental text content to
                enhance learning.
              </Alert>
            ) : (
              <Box className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {textContent.map((content) => (
                  <Box>
                    <Card variant="outlined">
                      <CardContent>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6">{content.name}</Typography>
                          </Box>

                          <Box>
                            <Tooltip title="Edit Content">
                              <IconButton
                                size="small"
                                onClick={() => onEditTextContent(content)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete Content">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  setDeleting(`text-${content.id}`)
                                }
                              >
                                <DeleteForeverIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        <ConfirmAction
                          executeAction={() => onDeleteTextContent(content.id)}
                          open={deleting}
                          setOpen={setDeleting}
                          openText={`text-${content.id}`}
                          title="Confirm Deletion"
                          text={`Are you sure you want to delete "${content.name}"?`}
                          onFailure={(err) =>
                            setErrorMessage(
                              err.message || 'Error deleting content',
                            )
                          }
                        />
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      {/* Show Notes Tab */}
      {activeTab === 'show-notes' && !selectedShowNotes && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                showNotesForm.resetForm()
                setFormOpen(true)
              }}
            >
              Create Show Notes
            </Button>
          </Box>

          {!showNotes || showNotes.length === 0 ? (
            <Alert severity="info">
              No show notes created yet. Create your first show notes to get
              started with the block-based editor.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {showNotes.map((notes) => (
                <Card
                  key={notes.id}
                  variant="outlined"
                  sx={{
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: (theme) => theme.shadows[4],
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      {/* Left section - Main info */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          flex: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                          }}
                        >
                          <TextContentIcon />
                        </Box>

                        <Box>
                          <Typography variant="h6" sx={{ mb: 0.5 }}>
                            {notes.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            {notes.description}
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {blockCounts[notes.id] ? (
                                <>
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                  >
                                    {blockCounts[notes.id].total} blocks
                                  </Typography>
                                  {blockCounts[notes.id].text > 0 && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                      }}
                                    >
                                      <TextContentIcon sx={{ fontSize: 12 }} />
                                      <Typography
                                        variant="caption"
                                        color="text.disabled"
                                      >
                                        {blockCounts[notes.id].text}
                                      </Typography>
                                    </Box>
                                  )}
                                  {blockCounts[notes.id].image > 0 && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                      }}
                                    >
                                      <ImageIcon sx={{ fontSize: 12 }} />
                                      <Typography
                                        variant="caption"
                                        color="text.disabled"
                                      >
                                        {blockCounts[notes.id].image}
                                      </Typography>
                                    </Box>
                                  )}
                                </>
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                >
                                  Loading...
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="caption" color="text.disabled">
                              Created{' '}
                              {notes.createdTime
                                ? new Date(
                                    notes.createdTime,
                                  ).toLocaleDateString()
                                : 'Unknown'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Right section - Actions */}
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => setSelectedShowNotes(notes)}
                          sx={{ minWidth: 'auto' }}
                        >
                          Edit
                        </Button>

                        <Tooltip title="Delete Show Notes">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setDeleting(`show-notes-${notes.id}`)
                            }
                          >
                            <DeleteForeverIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <ConfirmAction
                      executeAction={async () => {
                        await axios.delete(
                          `/api/v2/create/show-notes/${notes.id}`,
                        )
                        if (selectedClass) {
                          // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
                          await dispatch(fetchShowNotes(selectedClass.id))
                        }
                      }}
                      open={deleting}
                      setOpen={setDeleting}
                      openText={`show-notes-${notes.id}`}
                      title="Confirm Deletion"
                      text={`Are you sure you want to delete "${notes.name}" and all its content blocks?`}
                      onFailure={(err) =>
                        setErrorMessage(
                          err.message || 'Error deleting show notes',
                        )
                      }
                    />
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}
      {/* Show Notes Editor Tab */}
      {activeTab === 'show-notes' && selectedShowNotes && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h6">
                Editing: {selectedShowNotes.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedShowNotes.description}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<LeftArrowIcon />}
              onClick={() => setSelectedShowNotes(null)}
            >
              Back to Show Notes List
            </Button>
          </Box>

          <ShowNotesTab
            showNotesID={selectedShowNotes.id}
            showNotesName={selectedShowNotes.name}
            showNotesDescription={selectedShowNotes.description}
            showNotesContent={showNotesContent}
            setShowNotesContent={setShowNotesContent}
            showNotesSaving={showNotesSaving}
            setShowNotesSaving={setShowNotesSaving}
            showNotesOutput={showNotesOutput}
            setShowNotesOutput={setShowNotesOutput}
          />
        </Box>
      )}
      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/create/v4')}
            disabled={saving}
          >
            Save and Exit
          </Button>
          <Button
            variant="outlined"
            startIcon={<LeftArrowIcon />}
            onClick={() => setStep('title-screen')}
          >
            Back to Title Screen
          </Button>
        </Box>

        <Button
          variant="contained"
          endIcon={<RightArrowIcon />}
          onClick={() => setStep('quiz')}
        >
          Continue to Quiz
        </Button>
      </Box>
      {/* Video Form Modal */}
      <Dialog
        open={formOpen && activeTab === 'videos'}
        onClose={() => !saving && setFormOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {modalMode === 'upload'
            ? `Upload Video File - ${selectedVideoForUpload?.name}`
            : `${videoForm.values.id ? 'Edit' : 'Add'} Video`}
        </DialogTitle>

        {modalMode !== 'upload' ? (
          <form onSubmit={videoForm.handleSubmit}>
            <DialogContent>
              <TextField
                fullWidth
                label="Video Name"
                name="name"
                value={videoForm.values.name}
                onChange={videoForm.handleChange}
                error={videoForm.touched.name && !!videoForm.errors.name}
                helperText={videoForm.touched.name && videoForm.errors.name}
                sx={{ mb: 3 }}
                autoFocus
              />

              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={3}
                value={videoForm.values.description}
                onChange={videoForm.handleChange}
                error={
                  videoForm.touched.description &&
                  !!videoForm.errors.description
                }
                helperText={
                  videoForm.touched.description && videoForm.errors.description
                }
              />
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setFormOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving
                  ? 'Saving...'
                  : videoForm.values.id
                    ? 'Update'
                    : modalMode === 'create'
                      ? 'Create & Upload'
                      : 'Update'}
              </Button>
            </DialogActions>
          </form>
        ) : (
          <>
            <DialogContent>
              <Alert severity="info" sx={{ mb: 3 }}>
                Upload a video file for{' '}
                <strong>{selectedVideoForUpload?.name}</strong>. Supported
                formats: MP4, MOV, AVI (max 2GB)
              </Alert>

              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{
                  py: 4,
                  borderStyle: 'dashed',
                  bgcolor: file ? 'success.50' : 'background.main',
                  mb: 3,
                }}
              >
                {file ? (
                  <Box sx={{ textAlign: 'center' }}>
                    <VideoIcon
                      sx={{ fontSize: 48, color: 'success.main', mb: 1 }}
                    />
                    <Typography variant="h6">{file.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(file.size / 1024 / 1024).toFixed(1)} MB
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <VideoIcon
                      sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
                    />
                    <Typography variant="h6">Choose Video File</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Click to browse or drag and drop
                    </Typography>
                  </Box>
                )}
                <input
                  type="file"
                  accept="video/*"
                  hidden
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </Button>

              {uploading > 0 && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
                >
                  <CircularProgress
                    variant="determinate"
                    value={uploading}
                    size={24}
                  />
                  <Typography variant="body2">
                    Uploading... {uploading}%
                  </Typography>
                </Box>
              )}
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => {
                  setFormOpen(false)
                  setFile(null)
                  setSelectedVideoForUpload(null)
                }}
                disabled={uploading > 0}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleVideoUpload}
                disabled={!file || uploading > 0}
              >
                {uploading > 0 ? `Uploading ${uploading}%` : 'Upload Video'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      {/* Text Content Modal */}
      <Dialog
        open={formOpen && activeTab === 'text-content'}
        onClose={() => !saving && setFormOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {textForm.values.id ? 'Edit' : 'Add'} Text Content
        </DialogTitle>

        <FormikProvider value={textForm}>
          <form onSubmit={textForm.handleSubmit}>
            <DialogContent>
              <TextField
                fullWidth
                label="Content Name"
                name="name"
                value={textForm.values.name}
                onChange={textForm.handleChange}
                error={textForm.touched.name && !!textForm.errors.name}
                helperText={textForm.touched.name && textForm.errors.name}
                sx={{ mb: 3 }}
                autoFocus
              />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Content *
                </Typography>
                <Field name="content">
                  {({ field, meta }: FieldProps) => (
                    <Box>
                      <Wysiwyg
                        value={field.value}
                        onChange={(value: string) =>
                          textForm.setFieldValue('content', value)
                        }
                        defaultValue="Add supplemental text content or links here."
                      />
                      {meta.touched && meta.error && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 1, display: 'block' }}
                        >
                          {meta.error}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Field>
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setFormOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? 'Saving...' : textForm.values.id ? 'Update' : 'Add'}
              </Button>
            </DialogActions>
          </form>
        </FormikProvider>
      </Dialog>
      {/* Show Notes Modal */}
      <Dialog
        open={formOpen && activeTab === 'show-notes'}
        onClose={() => !saving && setFormOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create Show Notes</DialogTitle>

        <FormikProvider value={showNotesForm}>
          <form onSubmit={showNotesForm.handleSubmit}>
            <DialogContent>
              <TextField
                fullWidth
                label="Show Notes Name"
                name="name"
                value={showNotesForm.values.name}
                onChange={showNotesForm.handleChange}
                error={
                  showNotesForm.touched.name && !!showNotesForm.errors.name
                }
                helperText={
                  showNotesForm.touched.name && showNotesForm.errors.name
                }
                sx={{ mb: 3 }}
                autoFocus
              />

              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={3}
                value={showNotesForm.values.description}
                onChange={showNotesForm.handleChange}
                error={
                  showNotesForm.touched.description &&
                  !!showNotesForm.errors.description
                }
                helperText={
                  showNotesForm.touched.description &&
                  showNotesForm.errors.description
                }
              />
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setFormOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? 'Creating...' : 'Create Show Notes'}
              </Button>
            </DialogActions>
          </form>
        </FormikProvider>
      </Dialog>
      {/* Transcription Confirmation Dialog */}
      <Dialog
        open={!!transcribeConfirmOpen}
        onClose={() => setTranscribeConfirmOpen('')}
      >
        <DialogTitle>Confirm Manual Transcription</DialogTitle>
        <DialogContent>
          <Typography>
            Videos are automatically transcribed upon upload. If this is a newly
            uploaded video, there is no need to manually transcribe it. Please
            only use this for older videos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTranscribeConfirmOpen('')}>Cancel</Button>
          <Button
            onClick={() => {
              handleTranscribe(transcribeConfirmOpen)
              setTranscribeConfirmOpen('')
            }}
            variant="contained"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      {/* Vocabulary Management Modal */}
      <VocabularyManagementModal
        open={vocabularyModalOpen}
        onClose={handleCloseVocabularyModal}
        onAddVocabulary={handleOpenAddVocabularyModal}
      />
      {/* Add Vocabulary Modal */}
      <AddVocabularyModal
        open={addVocabularyModalOpen}
        onClose={handleCloseAddVocabularyModal}
        onSuccess={handleVocabularySuccess}
        initialWord={vocabularyInitialWord}
      />
      {/* Transcript Editor Modal */}
      <TranscriptEditorModal
        open={transcriptModalOpen}
        onClose={() => setTranscriptModalOpen(false)}
        videoId={selectedVideoId}
      />
      {/* Download Video Modal */}
      {selectedVideoForDownload && (
        <DownloadVideoModal
          open={downloadModalOpen}
          onClose={() => {
            setDownloadModalOpen(false)
            setSelectedVideoForDownload(null)
          }}
          videoId={selectedVideoForDownload.id}
          videoName={selectedVideoForDownload.name}
        />
      )}
      {errorMessage && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          onClose={() => setErrorMessage('')}
        >
          {errorMessage}
        </Alert>
      )}
    </Box>
  )
}
